import { useEffect, useRef, useCallback, useState } from 'react'
import {
  slack,
  SlackChannel,
  SlackMessage,
  formatSlackTimestamp,
  parseSlackMessage,
  isSlackConnected,
} from '../services/slack'
import { useNotifications } from './useNotifications'
const POLL_INTERVAL_MS = 30000 // 30 seconds
const LAST_SEEN_KEY = 'vsm_slack_last_seen'
const WATCHED_CHANNELS_KEY = 'vsm_slack_watched_channels'
interface SlackNotification {
  channelId: string
  channelName: string
  userName: string
  text: string
  ts: string
}
function getLastSeenTimestamps(): Record<string, string> {
  try {
    const stored = localStorage.getItem(LAST_SEEN_KEY)
    return stored ? JSON.parse(stored) : {}
  } catch {
    return {}
  }
}
function setLastSeenTimestamp(channelId: string, ts: string) {
  const current = getLastSeenTimestamps()
  current[channelId] = ts
  localStorage.setItem(LAST_SEEN_KEY, JSON.stringify(current))
}
export function getWatchedChannels(): string[] {
  try {
    const stored = localStorage.getItem(WATCHED_CHANNELS_KEY)
    return stored ? JSON.parse(stored) : []
  } catch {
    return []
  }
}
export function setWatchedChannels(channelIds: string[]) {
  localStorage.setItem(WATCHED_CHANNELS_KEY, JSON.stringify(channelIds))
}
export function addWatchedChannel(channelId: string) {
  const current = getWatchedChannels()
  if (!current.includes(channelId)) {
    setWatchedChannels([...current, channelId])
  }
}
export function removeWatchedChannel(channelId: string) {
  const current = getWatchedChannels()
  setWatchedChannels(current.filter((id) => id !== channelId))
}
export function useSlackPolling(enabled: boolean = true) {
  const { push } = useNotifications()
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const [newMessageCount, setNewMessageCount] = useState(0)
  const [lastNotifications, setLastNotifications] = useState<
    SlackNotification[]
  >([])
  const channelNamesRef = useRef<Record<string, string>>({})
  const isPollingRef = useRef(false)
  const pollChannels = useCallback(async () => {
    if (!isSlackConnected() || isPollingRef.current) return
    isPollingRef.current = true
    try {
      const watchedIds = getWatchedChannels()
      if (watchedIds.length === 0) {
        // If no channels are watched, auto-watch all channels on first run
        const channels = await slack.getChannels()
        if (channels.length > 0) {
          const ids = channels.slice(0, 10).map((c) => c.id) // Watch first 10
          setWatchedChannels(ids)
          channels.forEach((c) => {
            channelNamesRef.current[c.id] = c.name
          })
          // Set initial timestamps so we don't flood with old messages
          for (const ch of channels.slice(0, 10)) {
            const msgs = await slack.getChannelHistory(ch.id, 1)
            if (msgs.length > 0) {
              setLastSeenTimestamp(ch.id, msgs[0].ts)
            }
          }
        }
        isPollingRef.current = false
        return
      }

      // Ensure we have channel names
      if (Object.keys(channelNamesRef.current).length === 0) {
        const channels = await slack.getChannels()
        channels.forEach((c) => {
          channelNamesRef.current[c.id] = c.name
        })
      }
      const lastSeen = getLastSeenTimestamps()
      const newNotifs: SlackNotification[] = []
      for (const channelId of watchedIds) {
        try {
          const messages = await slack.getChannelHistory(channelId, 5)
          if (messages.length === 0) continue
          const lastSeenTs = lastSeen[channelId]
          const newestTs = messages[0].ts // messages come newest first

          if (!lastSeenTs) {
            // First time seeing this channel, just record the timestamp
            setLastSeenTimestamp(channelId, newestTs)
            continue
          }

          // Find new messages (newer than lastSeen)
          const newMessages = messages.filter(
            (m) => parseFloat(m.ts) > parseFloat(lastSeenTs),
          )
          if (newMessages.length > 0) {
            // Update last seen to newest
            setLastSeenTimestamp(channelId, newestTs)
            for (const msg of newMessages) {
              // Skip bot messages from our own bot
              if (msg.bot_id) continue
              let userName = 'Alguien'
              if (msg.user) {
                const user = await slack.getUserInfo(msg.user)
                if (user) {
                  userName =
                    user.profile?.display_name || user.real_name || user.name
                }
              }
              const channelName =
                channelNamesRef.current[channelId] || channelId
              newNotifs.push({
                channelId,
                channelName,
                userName,
                text: parseSlackMessage(msg.text).slice(0, 100),
                ts: msg.ts,
              })

              // Push to notification center
              push({
                type: 'integration',
                title: `💬 #${channelName}`,
                message: `${userName}: ${parseSlackMessage(msg.text).slice(0, 80)}`,
                source: 'Slack',
              })
            }
          }
        } catch (err) {
          console.error(
            `[SlackPolling] Error polling channel ${channelId}:`,
            err,
          )
        }
      }
      if (newNotifs.length > 0) {
        setNewMessageCount((prev) => prev + newNotifs.length)
        setLastNotifications(newNotifs)

        // Browser notification (if permitted)
        if (Notification.permission === 'granted') {
          const first = newNotifs[0]
          new Notification(`Slack - #${first.channelName}`, {
            body: `${first.userName}: ${first.text}`,
            icon: '💬',
            tag: 'slack-notification',
          })
        }
      }
    } catch (err) {
      console.error('[SlackPolling] Error:', err)
    } finally {
      isPollingRef.current = false
    }
  }, [push])

  // Request notification permission
  useEffect(() => {
    if (enabled && isSlackConnected() && 'Notification' in window) {
      if (Notification.permission === 'default') {
        Notification.requestPermission()
      }
    }
  }, [enabled])

  // Start/stop polling
  useEffect(() => {
    if (!enabled || !isSlackConnected()) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
        intervalRef.current = null
      }
      return
    }

    // Initial poll after 5 seconds (give time for app to load)
    const initialTimeout = setTimeout(() => {
      pollChannels()
    }, 5000)

    // Regular polling
    intervalRef.current = setInterval(pollChannels, POLL_INTERVAL_MS)
    return () => {
      clearTimeout(initialTimeout)
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
        intervalRef.current = null
      }
    }
  }, [enabled, pollChannels])
  const clearCount = useCallback(() => {
    setNewMessageCount(0)
  }, [])

  // Mark a channel as "seen" (reset its timestamp to now)
  const markChannelSeen = useCallback((channelId: string) => {
    const now = (Date.now() / 1000).toString()
    setLastSeenTimestamp(channelId, now)
  }, [])
  return {
    newMessageCount,
    lastNotifications,
    clearCount,
    markChannelSeen,
    pollNow: pollChannels,
  }
}
